/**
 * Buildings and landmarks (temple, ruins, castle, rondavels, lighthouse,
 * pagoda, galleon, treehouse, festival tents, sea arch) and the rope bridge.
 *
 * Each structure is drawn around its base centre (0, 0) with "up" = −y, in
 * units of `s` = the structure's overall height in px.
 */
import type { BridgeLayer, MoundStyle, StructureLayer, StructureType } from '@dropstack/core';
import { rimWidth, smoothstep, type SceneSize } from '../layout.ts';
import { hash01, valueNoise } from '../noise.ts';
import {
  bodyGradient,
  circleBlob,
  circlePath,
  ellipsePath,
  gloss,
  glow,
  leafPath,
  mix,
  quadPoint,
  ribbonPath,
  rgba,
  rimOf,
  roundRectPath,
  shade,
  smoothLine,
  strokeRim,
  vGradient,
  type Circle,
  type Ctx,
  type Point,
} from './draw.ts';
import type { PaintEnv, Painter } from './types.ts';

interface Build {
  /** Overall height in px. */
  s: number;
  color: string;
  accent: string;
  trim: string;
  size: SceneSize;
  seed: number;
  time: number;
}

// ───────────── shared parts ─────────────

/** Horizontal cylinder shading: lit on the left, shaded on the right. */
function cylinder(ctx: Ctx, x0: number, x1: number, color: string): CanvasGradient {
  const g = ctx.createLinearGradient(x0, 0, x1, 0);
  g.addColorStop(0, shade(color, 0.08));
  g.addColorStop(0.35, shade(color, 0.18));
  g.addColorStop(1, shade(color, -0.16));
  return g;
}

/** Fill + rim a rounded box. */
function box(ctx: Ctx, x: number, y: number, w: number, h: number, r: number, fill: string | CanvasGradient, rim: string, size: SceneSize): void {
  ctx.beginPath();
  roundRectPath(ctx, x, y, w, h, r);
  ctx.fillStyle = fill;
  ctx.fill();
  strokeRim(ctx, rim, size);
}

/** Arched opening (doors, windows): flat bottom at y, round top. */
function archPath(ctx: Ctx, cx: number, y: number, w: number, h: number): void {
  const r = w / 2;
  ctx.moveTo(cx - r, y);
  ctx.lineTo(cx - r, y - h + r);
  ctx.arc(cx, y - h + r, r, Math.PI, 0);
  ctx.lineTo(cx + r, y);
  ctx.closePath();
}

function opening(ctx: Ctx, cx: number, y: number, w: number, h: number, fill: string, rim: string, size: SceneSize): void {
  ctx.beginPath();
  archPath(ctx, cx, y, w, h);
  ctx.fillStyle = fill;
  ctx.fill();
  strokeRim(ctx, rim, size, 0.8);
}

/** Conical roof (towers, rondavels): eaves at y, apex at y − h, slightly bulging sides. */
function coneRoof(ctx: Ctx, cx: number, y: number, w: number, h: number, color: string, b: Build, bulge = 0.08): void {
  ctx.beginPath();
  ctx.moveTo(cx - w / 2, y);
  ctx.quadraticCurveTo(cx - w * (0.25 + bulge), y - h * 0.55, cx, y - h);
  ctx.quadraticCurveTo(cx + w * (0.25 + bulge), y - h * 0.55, cx + w / 2, y);
  ctx.quadraticCurveTo(cx, y + h * 0.1, cx - w / 2, y);
  ctx.closePath();
  ctx.fillStyle = cylinder(ctx, cx - w / 2, cx + w / 2, color);
  ctx.fill();
  strokeRim(ctx, rimOf(color), b.size);
  gloss(ctx, cx - w * 0.16, y - h * 0.45, w * 0.06, h * 0.2, 0.35, 0.35);
}

/** A little pennant on a pole. */
function pennant(ctx: Ctx, x: number, y: number, h: number, color: string, b: Build): void {
  const wave = b.time ? Math.sin(b.time * 2.2 + x) * h * 0.05 : 0;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x, y - h);
  ctx.strokeStyle = shade(color, -0.5);
  ctx.lineWidth = Math.max(1, h * 0.06);
  ctx.lineCap = 'round';
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x, y - h);
  ctx.quadraticCurveTo(x + h * 0.3, y - h * 0.95 + wave, x + h * 0.6, y - h * 0.82 + wave);
  ctx.quadraticCurveTo(x + h * 0.3, y - h * 0.75 - wave, x, y - h * 0.62);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  strokeRim(ctx, rimOf(color), b.size, 0.7);
}

/**
 * Shape of a knoll or summit under a structure, relative to its base line
 * (y = 0, up = −y). `crown` is how far the top bulges above the base line,
 * `depth` how far the slopes fall below it (they have faded out by then).
 */
interface MoundShape {
  /** Half-width of the flat-ish top the building stands on. */
  top: number;
  /** Half-width at the foot of the slopes. */
  foot: number;
  crown: number;
  depth: number;
}

function moundShape(style: Exclude<MoundStyle, 'cloud'>, footprint: number, s: number): MoundShape {
  if (style === 'peak') return { top: footprint * 0.5, foot: footprint * 1.35, crown: s * 0.02, depth: s * 1.05 };
  return { top: footprint * 0.5, foot: footprint * 0.95, crown: s * 0.035, depth: footprint * 0.17 };
}

/** Largest downward wobble of a rocky crest, as a fraction of `depth`. */
const ROCK_JAG = 0.035;

/** Silhouette of a knoll/summit from left foot to right foot, sampled left to right. */
function moundCrest(m: MoundShape, seed: number, rocky: boolean): Point[] {
  const n = 28;
  const pts: Point[] = [];
  for (let i = 0; i <= n; i++) {
    const x = -m.foot + (2 * m.foot * i) / n;
    const d = Math.abs(x);
    // 1 across the top, easing to 0 at the foot; a gentle dome on the crown.
    const rise = 1 - smoothstep(m.top, m.foot, d);
    const dome = d < m.top ? m.crown * (1 - (d / m.top) ** 2) : 0;
    const shape = rocky ? rise ** 0.8 : rise;
    const jag = rocky && d > m.top ? m.depth * ROCK_JAG * valueNoise(seed + 5, i * 0.9) : 0;
    pts.push({ x, y: m.depth * (1 - shape) - dome + jag });
  }
  return pts;
}

/**
 * Ground the structure stands on. Hills and summits melt into whatever is
 * behind them (their lower part fades out, so there is no hard bottom edge);
 * a cloud platform is a puffy blob with a soft lavender rim.
 */
function mound(ctx: Ctx, style: MoundStyle, footprint: number, s: number, color: string, size: SceneSize, seed: number): void {
  if (style === 'cloud') {
    const n = 7;
    const circles: Circle[] = Array.from({ length: n }, (_, i) => {
      const u = i / (n - 1) - 0.5;
      const r = footprint * (0.2 - Math.abs(u) * 0.09) * (0.9 + 0.2 * hash01(seed + 3, i));
      return { x: u * footprint * 1.25, y: r * 0.62 + footprint * 0.03, r };
    });
    const g = vGradient(ctx, -footprint * 0.12, footprint * 0.3, [color, [0.55, color], mix(color, '#d6d3ff', 0.55)]);
    circleBlob(ctx, circles, g, rgba(mix(color, '#9c96d8', 0.6), 0.75), size, 0.8);
    gloss(ctx, -footprint * 0.3, footprint * 0.02, footprint * 0.12, footprint * 0.035, 0.7, -0.15);
    return;
  }
  const m = moundShape(style, footprint, s);
  const rocky = style === 'peak';
  const crest = moundCrest(m, seed, rocky);
  const body = (): void => {
    ctx.beginPath();
    smoothLine(ctx, crest);
    ctx.closePath();
  };
  // Opaque on top, melting away completely by the feet: no hard edges anywhere below the crest.
  const at = (y: number) => (y + m.crown) / (m.depth + m.crown);
  body();
  ctx.fillStyle = vGradient(ctx, -m.crown, m.depth, [
    shade(color, 0.14),
    [at(m.depth * 0.15), color],
    [at(m.depth * 0.5), shade(color, -0.05)],
    [at(m.depth * 0.8), rgba(shade(color, -0.07), 0.45)],
    rgba(shade(color, -0.08), 0),
  ]);
  ctx.fill();
  if (rocky) {
    // Weathered gullies running down the slopes.
    ctx.save();
    body();
    ctx.clip();
    ctx.strokeStyle = rgba(shade(color, -0.3), 0.35);
    ctx.lineWidth = Math.max(1, s * 0.01);
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (let k = 0; k < 6; k++) {
      const side = k % 2 === 0 ? -1 : 1;
      const x0 = side * m.top * (0.7 + 0.5 * hash01(seed + 11, k));
      const y0 = m.depth * (0.08 + 0.3 * hash01(seed + 13, k));
      ctx.moveTo(x0, y0);
      ctx.quadraticCurveTo(x0 + side * m.foot * 0.12, y0 + m.depth * 0.25, x0 + side * m.foot * 0.3, y0 + m.depth * 0.55);
    }
    ctx.stroke();
    ctx.restore();
  }
  // Rim along the top silhouette only, fading out down the slopes.
  ctx.beginPath();
  smoothLine(ctx, crest);
  const rim = rimOf(color);
  ctx.strokeStyle = vGradient(ctx, -m.crown, m.depth, [rim, [0.55, rgba(rim, 0.7)], rgba(rim, 0)]);
  ctx.lineWidth = rimWidth(size);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.stroke();
  gloss(ctx, -m.top * 0.55, m.depth * 0.08 - m.crown * 0.3, m.top * 0.3, Math.max(1, m.crown * 0.5 + s * 0.012), 0.35, -0.08);
}

/** Little leafy drapes (vines/moss) hanging off an edge. */
function drapes(ctx: Ctx, at: readonly Point[], len: number, color: string, size: SceneSize): void {
  ctx.strokeStyle = shade(color, -0.2);
  ctx.lineWidth = Math.max(1, len * 0.06);
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (const p of at) {
    ctx.moveTo(p.x, p.y);
    ctx.quadraticCurveTo(p.x + len * 0.2, p.y + len * 0.5, p.x, p.y + len);
  }
  ctx.stroke();
  ctx.beginPath();
  for (const p of at) {
    for (let k = 1; k <= 3; k++) {
      const side = k % 2 ? 1 : -1;
      leafPath(ctx, p.x + side * len * 0.12, p.y + len * k * 0.28, len * 0.3, len * 0.1, side > 0 ? 0.5 : Math.PI - 0.5);
    }
  }
  const clumps: Circle[] = at.map((p) => ({ x: p.x, y: p.y, r: len * 0.18 }));
  ctx.fillStyle = color;
  ctx.fill();
  circleBlob(ctx, clumps, color, rimOf(color), size, 0.6);
}

// ───────────── structures ─────────────

function temple(ctx: Ctx, b: Build): void {
  const { s } = b;
  const tiers = 4;
  const tierH = s * 0.14;
  const rim = rimOf(b.color);
  for (let k = 0; k < tiers; k++) {
    const w = s * (1.1 - k * 0.19);
    const y = -k * tierH;
    const tier = () => {
      ctx.beginPath();
      roundRectPath(ctx, -w / 2, y - tierH, w, tierH, s * 0.015);
    };
    tier();
    ctx.fillStyle = bodyGradient(ctx, b.color, y - tierH, y, 0.12, -0.08);
    ctx.fill();
    ctx.save();
    ctx.clip();
    ctx.fillStyle = rgba(shade(b.color, -0.35), 0.22);
    ctx.fillRect(w * 0.22, y - tierH, w * 0.3, tierH);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.fillRect(-w / 2, y - tierH, w, Math.max(1, tierH * 0.12));
    ctx.restore();
    tier();
    strokeRim(ctx, rim, b.size);
  }
  // Central staircase.
  const top = -tiers * tierH;
  ctx.beginPath();
  ctx.moveTo(-s * 0.11, 0);
  ctx.lineTo(-s * 0.07, top);
  ctx.lineTo(s * 0.07, top);
  ctx.lineTo(s * 0.11, 0);
  ctx.closePath();
  ctx.fillStyle = shade(b.color, 0.14);
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.strokeStyle = rgba(shade(b.color, -0.25), 0.6);
  ctx.lineWidth = Math.max(1, s * 0.006);
  ctx.beginPath();
  for (let y = -tierH / 3; y > top; y -= tierH / 3) {
    ctx.moveTo(-s * 0.12, y);
    ctx.lineTo(s * 0.12, y);
  }
  ctx.stroke();
  ctx.restore();
  ctx.beginPath();
  ctx.moveTo(-s * 0.11, 0);
  ctx.lineTo(-s * 0.07, top);
  ctx.lineTo(s * 0.07, top);
  ctx.lineTo(s * 0.11, 0);
  strokeRim(ctx, rim, b.size, 0.8);
  // Vines on the tier edges.
  const drapePts: Point[] = [];
  for (let k = 0; k < tiers - 1; k++) {
    const w = s * (1.1 - k * 0.19);
    const y = -(k + 1) * tierH;
    for (const u of [-0.42, 0.3]) if (hash01(b.seed, k * 3 + (u > 0 ? 1 : 0)) > 0.3) drapePts.push({ x: u * w, y });
  }
  drapes(ctx, drapePts, tierH * 0.8, b.trim, b.size);
  // Shrine with a golden roof and sun emblem.
  const sw = s * 0.34;
  const sh = s * 0.19;
  box(ctx, -sw / 2, top - sh, sw, sh, s * 0.012, bodyGradient(ctx, shade(b.color, 0.08), top - sh, top), rim, b.size);
  opening(ctx, 0, top, sw * 0.34, sh * 0.72, shade(b.color, -0.38), rim, b.size);
  const ry = top - sh;
  ctx.beginPath();
  ctx.moveTo(-sw * 0.62, ry + s * 0.01);
  ctx.lineTo(-sw * 0.3, ry - s * 0.09);
  ctx.lineTo(sw * 0.3, ry - s * 0.09);
  ctx.lineTo(sw * 0.62, ry + s * 0.01);
  ctx.closePath();
  ctx.fillStyle = bodyGradient(ctx, b.accent, ry - s * 0.09, ry, 0.25, -0.05);
  ctx.fill();
  strokeRim(ctx, rimOf(b.accent), b.size);
  const ey = ry - s * 0.14;
  glow(ctx, 0, ey, s * 0.16, b.accent, 0.45);
  ctx.beginPath();
  circlePath(ctx, 0, ey, s * 0.05);
  ctx.fillStyle = bodyGradient(ctx, b.accent, ey - s * 0.05, ey + s * 0.05, 0.35, -0.05);
  ctx.fill();
  strokeRim(ctx, rimOf(b.accent), b.size);
  gloss(ctx, -s * 0.015, ey - s * 0.02, s * 0.015, s * 0.008, 0.7);
}

function ruins(ctx: Ctx, b: Build): void {
  const { s } = b;
  const rim = rimOf(b.color);
  // Archway (behind the columns).
  const x0 = s * 0.02;
  const x1 = s * 0.42;
  const pw = s * 0.08;
  const hp = s * 0.46;
  const cx = (x0 + x1) / 2;
  const R = (x1 - x0) / 2;
  const arch = () => {
    ctx.beginPath();
    ctx.moveTo(x0, 0);
    ctx.lineTo(x0, -hp);
    ctx.arc(cx, -hp, R, Math.PI, 0);
    ctx.lineTo(x1, 0);
    ctx.lineTo(x1 - pw, 0);
    ctx.lineTo(x1 - pw, -hp);
    ctx.arc(cx, -hp, R - pw, 0, Math.PI, true);
    ctx.lineTo(x0 + pw, 0);
    ctx.closePath();
  };
  arch();
  ctx.fillStyle = bodyGradient(ctx, b.color, -hp - R, 0);
  ctx.fill();
  strokeRim(ctx, rim, b.size);
  // Golden keystone.
  ctx.beginPath();
  ctx.moveTo(cx - pw * 0.45, -hp - R);
  ctx.lineTo(cx + pw * 0.45, -hp - R);
  ctx.lineTo(cx + pw * 0.3, -hp - R + pw * 1.05);
  ctx.lineTo(cx - pw * 0.3, -hp - R + pw * 1.05);
  ctx.closePath();
  ctx.fillStyle = bodyGradient(ctx, b.accent, -hp - R, -hp - R + pw, 0.3, -0.05);
  ctx.fill();
  strokeRim(ctx, rimOf(b.accent), b.size, 0.8);

  const column = (x: number, h: number, broken: boolean) => {
    const cw = s * 0.11;
    box(ctx, x - cw * 0.7, -s * 0.05, cw * 1.4, s * 0.05, s * 0.01, bodyGradient(ctx, b.color, -s * 0.05, 0), rim, b.size);
    ctx.beginPath();
    ctx.moveTo(x - cw / 2, -s * 0.05);
    if (broken) {
      ctx.lineTo(x - cw / 2, -h + s * 0.02);
      ctx.lineTo(x - cw * 0.15, -h - s * 0.03);
      ctx.lineTo(x + cw * 0.05, -h + s * 0.01);
      ctx.lineTo(x + cw * 0.3, -h - s * 0.015);
      ctx.lineTo(x + cw / 2, -h + s * 0.03);
    } else {
      ctx.lineTo(x - cw / 2, -h + s * 0.06);
      ctx.lineTo(x + cw / 2, -h + s * 0.06);
    }
    ctx.lineTo(x + cw / 2, -s * 0.05);
    ctx.closePath();
    ctx.fillStyle = cylinder(ctx, x - cw / 2, x + cw / 2, b.color);
    ctx.fill();
    ctx.save();
    ctx.clip();
    ctx.strokeStyle = rgba(shade(b.color, -0.25), 0.5);
    ctx.lineWidth = Math.max(1, s * 0.006);
    ctx.beginPath();
    for (const u of [-0.2, 0.12]) {
      ctx.moveTo(x + u * cw, -s * 0.05);
      ctx.lineTo(x + u * cw, -h);
    }
    ctx.stroke();
    ctx.restore();
    strokeRim(ctx, rim, b.size);
    if (!broken) box(ctx, x - cw * 0.75, -h, cw * 1.5, s * 0.06, s * 0.012, bodyGradient(ctx, shade(b.color, 0.08), -h, -h + s * 0.06), rim, b.size);
  };
  column(-s * 0.4, s * 0.95, false);
  column(-s * 0.14, s * 0.62, true);
  column(s * 0.56, s * 0.36, true);
  // Fallen blocks.
  for (const [bx, rot, w] of [
    [-s * 0.02, 0.2, s * 0.13],
    [s * 0.66, -0.15, s * 0.1],
  ] as const) {
    ctx.save();
    ctx.translate(bx, -w * 0.3);
    ctx.rotate(rot);
    box(ctx, -w / 2, -w * 0.3, w, w * 0.6, w * 0.12, bodyGradient(ctx, b.color, -w * 0.3, w * 0.3), rim, b.size);
    ctx.restore();
  }
  drapes(
    ctx,
    [
      { x: -s * 0.4, y: -s * 0.93 },
      { x: cx - R * 0.6, y: -hp - R * 0.8 },
      { x: cx + R * 0.7, y: -hp - R * 0.7 },
    ],
    s * 0.2,
    b.trim,
    b.size,
  );
}

function castle(ctx: Ctx, b: Build): void {
  const { s } = b;
  const rim = rimOf(b.color);
  const windowColor = mix(shade(b.accent, -0.45), '#2a2f5a', 0.35);
  const tower = (cx: number, w: number, h: number, roofH: number, flag: boolean) => {
    box(ctx, cx - w / 2, -h, w, h, s * 0.01, cylinder(ctx, cx - w / 2, cx + w / 2, b.color), rim, b.size);
    opening(ctx, cx, -h * 0.72, w * 0.32, w * 0.5, windowColor, rim, b.size);
    coneRoof(ctx, cx, -h + s * 0.005, w * 1.3, roofH, b.accent, b, 0.02);
    if (flag) pennant(ctx, cx, -h - roofH + s * 0.01, s * 0.1, b.trim, b);
  };
  // Outer towers, curtain wall, inner turrets, keep.
  tower(-s * 0.4, s * 0.15, s * 0.52, s * 0.18, true);
  tower(s * 0.4, s * 0.15, s * 0.5, s * 0.18, true);
  const wallTop = -s * 0.36;
  ctx.beginPath();
  ctx.moveTo(-s * 0.36, 0);
  ctx.lineTo(-s * 0.36, wallTop);
  const merlons = 7;
  const mw = (s * 0.72) / (merlons * 2 - 1);
  for (let i = 0; i < merlons * 2 - 1; i++) {
    const x = -s * 0.36 + i * mw;
    const up = i % 2 === 0;
    if (up) {
      ctx.lineTo(x, wallTop - s * 0.04);
      ctx.lineTo(x + mw, wallTop - s * 0.04);
    } else {
      ctx.lineTo(x, wallTop);
      ctx.lineTo(x + mw, wallTop);
    }
  }
  ctx.lineTo(s * 0.36, wallTop);
  ctx.lineTo(s * 0.36, 0);
  ctx.closePath();
  ctx.fillStyle = bodyGradient(ctx, b.color, wallTop, 0);
  ctx.fill();
  ctx.lineJoin = 'round';
  strokeRim(ctx, rim, b.size);
  tower(-s * 0.2, s * 0.12, s * 0.58, s * 0.16, false);
  tower(s * 0.2, s * 0.12, s * 0.6, s * 0.16, false);
  tower(0, s * 0.2, s * 0.74, s * 0.24, true);
  // Gate with a golden frame.
  opening(ctx, 0, 0, s * 0.13, s * 0.19, mix('#8b5a3c', b.color, 0.15), rimOf(b.trim), b.size);
  ctx.beginPath();
  archPath(ctx, 0, 0, s * 0.13, s * 0.19);
  ctx.strokeStyle = b.trim;
  ctx.lineWidth = Math.max(1.5, s * 0.012);
  ctx.stroke();
  for (const x of [-s * 0.29, s * 0.29]) opening(ctx, x, -s * 0.16, s * 0.05, s * 0.08, windowColor, rim, b.size);
}

function rondavel(ctx: Ctx, x: number, S: number, b: Build): void {
  const w = S * 0.72;
  const wh = S * 0.42;
  const wall = () => {
    ctx.beginPath();
    ctx.moveTo(x - w / 2, -wh);
    ctx.lineTo(x - w / 2, 0);
    ctx.quadraticCurveTo(x, S * 0.07, x + w / 2, 0);
    ctx.lineTo(x + w / 2, -wh);
    ctx.quadraticCurveTo(x, -wh + S * 0.07, x - w / 2, -wh);
    ctx.closePath();
  };
  wall();
  ctx.fillStyle = cylinder(ctx, x - w / 2, x + w / 2, b.color);
  ctx.fill();
  ctx.save();
  ctx.clip();
  // Painted band with a zig-zag, following the curve of the wall.
  const bandTop = -wh * 0.52;
  const bandH = wh * 0.16;
  ctx.beginPath();
  ctx.moveTo(x - w / 2, bandTop);
  ctx.quadraticCurveTo(x, bandTop + S * 0.07, x + w / 2, bandTop);
  ctx.lineTo(x + w / 2, bandTop + bandH);
  ctx.quadraticCurveTo(x, bandTop + bandH + S * 0.07, x - w / 2, bandTop + bandH);
  ctx.closePath();
  ctx.fillStyle = b.trim;
  ctx.fill();
  ctx.beginPath();
  const teeth = 6;
  for (let i = 0; i < teeth; i++) {
    const u0 = i / teeth - 0.5;
    const u1 = (i + 0.5) / teeth - 0.5;
    const u2 = (i + 1) / teeth - 0.5;
    const sag = (u: number) => (1 - (2 * u) ** 2) * S * 0.035;
    ctx.moveTo(x + u0 * w, bandTop + bandH * 0.85 + sag(u0));
    ctx.lineTo(x + u1 * w, bandTop + bandH * 0.15 + sag(u1));
    ctx.lineTo(x + u2 * w, bandTop + bandH * 0.85 + sag(u2));
  }
  ctx.strokeStyle = shade(b.trim, -0.35);
  ctx.lineWidth = Math.max(1, S * 0.012);
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.restore();
  wall();
  strokeRim(ctx, rimOf(b.color), b.size);
  opening(ctx, x + w * 0.1, S * 0.03, w * 0.24, wh * 0.62, shade(b.color, -0.5), rimOf(b.color), b.size);
  // Thatched cone roof with a scalloped fringe.
  const eave = -wh + S * 0.03;
  const rw = w * 1.24;
  coneRoof(ctx, x, eave, rw, S * 0.62, b.accent, b, 0.1);
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x - rw / 2, eave);
  ctx.quadraticCurveTo(x - rw * 0.35, eave - S * 0.34, x, eave - S * 0.62);
  ctx.quadraticCurveTo(x + rw * 0.35, eave - S * 0.34, x + rw / 2, eave);
  ctx.quadraticCurveTo(x, eave + S * 0.062, x - rw / 2, eave);
  ctx.clip();
  ctx.strokeStyle = rgba(shade(b.accent, -0.28), 0.55);
  ctx.lineWidth = Math.max(1, S * 0.01);
  ctx.beginPath();
  for (let i = -4; i <= 4; i++) {
    ctx.moveTo(x, eave - S * 0.62);
    ctx.lineTo(x + (i / 4) * rw * 0.5, eave + S * 0.05);
  }
  ctx.stroke();
  ctx.restore();
  ctx.beginPath();
  circlePath(ctx, x, eave - S * 0.62, S * 0.035);
  ctx.fillStyle = shade(b.accent, -0.25);
  ctx.fill();
}

function huts(ctx: Ctx, b: Build): void {
  // Back huts first, the big one in front.
  rondavel(ctx, -b.s * 0.9, b.s * 0.78, b);
  rondavel(ctx, b.s * 0.85, b.s * 0.7, b);
  rondavel(ctx, 0, b.s, b);
}

function lighthouse(ctx: Ctx, b: Build): void {
  const { s } = b;
  const rim = rimOf(b.color);
  // Keeper's cottage behind the tower.
  box(ctx, s * 0.06, -s * 0.16, s * 0.24, s * 0.16, s * 0.01, bodyGradient(ctx, b.color, -s * 0.16, 0), rim, b.size);
  ctx.beginPath();
  ctx.moveTo(s * 0.03, -s * 0.155);
  ctx.lineTo(s * 0.18, -s * 0.26);
  ctx.lineTo(s * 0.33, -s * 0.155);
  ctx.closePath();
  ctx.fillStyle = bodyGradient(ctx, b.accent, -s * 0.26, -s * 0.15);
  ctx.fill();
  strokeRim(ctx, rimOf(b.accent), b.size);
  opening(ctx, s * 0.24, 0, s * 0.05, s * 0.09, shade(b.accent, -0.4), rim, b.size);
  // Tapered tower with red bands.
  const h = s * 0.7;
  const tower = () => {
    ctx.beginPath();
    ctx.moveTo(-s * 0.13, 0);
    ctx.lineTo(-s * 0.085, -h);
    ctx.lineTo(s * 0.085, -h);
    ctx.lineTo(s * 0.13, 0);
    ctx.closePath();
  };
  tower();
  ctx.fillStyle = cylinder(ctx, -s * 0.13, s * 0.13, b.color);
  ctx.fill();
  ctx.save();
  ctx.clip();
  for (const [y0, y1] of [
    [0.12, 0.22],
    [0.34, 0.44],
    [0.56, 0.66],
  ] as const) {
    ctx.fillStyle = cylinder(ctx, -s * 0.13, s * 0.13, b.accent);
    ctx.fillRect(-s * 0.2, -s * y1, s * 0.4, s * (y1 - y0));
  }
  ctx.restore();
  tower();
  strokeRim(ctx, rim, b.size);
  opening(ctx, 0, 0, s * 0.07, s * 0.11, shade(b.accent, -0.45), rim, b.size);
  for (const y of [-s * 0.3, -s * 0.5]) {
    ctx.beginPath();
    ellipsePath(ctx, 0, y, s * 0.02, s * 0.03);
    ctx.fillStyle = shade(b.accent, -0.5);
    ctx.fill();
  }
  // Lamp room, gallery and dome.
  const galY = -h;
  const lampTop = galY - s * 0.15;
  glow(ctx, 0, galY - s * 0.075, s * 0.3, b.trim, 0.55);
  box(ctx, -s * 0.075, lampTop, s * 0.15, s * 0.12, s * 0.01, vGradient(ctx, lampTop, galY, [shade(b.trim, 0.5), b.trim]), shade(b.accent, -0.4), b.size);
  ctx.beginPath();
  for (const u of [-0.025, 0.025]) {
    ctx.moveTo(u * s, lampTop);
    ctx.lineTo(u * s, lampTop + s * 0.12);
  }
  ctx.strokeStyle = shade(b.accent, -0.4);
  ctx.lineWidth = Math.max(1, s * 0.008);
  ctx.stroke();
  box(ctx, -s * 0.135, galY - s * 0.03, s * 0.27, s * 0.04, s * 0.01, shade(b.accent, -0.3), rimOf(shade(b.accent, -0.3)), b.size);
  ctx.beginPath();
  ctx.moveTo(-s * 0.09, lampTop + s * 0.005);
  ctx.quadraticCurveTo(-s * 0.09, lampTop - s * 0.1, 0, lampTop - s * 0.1);
  ctx.quadraticCurveTo(s * 0.09, lampTop - s * 0.1, s * 0.09, lampTop + s * 0.005);
  ctx.closePath();
  ctx.fillStyle = bodyGradient(ctx, b.accent, lampTop - s * 0.1, lampTop, 0.2, -0.1);
  ctx.fill();
  strokeRim(ctx, rimOf(b.accent), b.size);
  gloss(ctx, -s * 0.035, lampTop - s * 0.06, s * 0.02, s * 0.012, 0.6);
  ctx.beginPath();
  circlePath(ctx, 0, lampTop - s * 0.115, s * 0.018);
  ctx.fillStyle = b.trim;
  ctx.fill();
  strokeRim(ctx, rimOf(b.trim), b.size, 0.6);
}

function pagoda(ctx: Ctx, b: Build): void {
  const { s } = b;
  const rim = rimOf(b.color);
  const bodies = [0.46, 0.38, 0.31, 0.24];
  const heights = [0.17, 0.14, 0.12, 0.1];
  const rt = s * 0.05;
  let y = 0;
  bodies.forEach((bw, k) => {
    const w = s * bw;
    const h = s * heights[k]!;
    box(ctx, -w / 2, y - h, w, h + 1, s * 0.006, cylinder(ctx, -w / 2, w / 2, b.color), rim, b.size);
    if (k === 0) opening(ctx, 0, y, w * 0.3, h * 0.8, shade(b.color, -0.5), rim, b.size);
    else {
      ctx.beginPath();
      circlePath(ctx, 0, y - h * 0.5, h * 0.18);
      ctx.fillStyle = b.trim;
      ctx.fill();
      strokeRim(ctx, rimOf(b.trim), b.size, 0.6);
    }
    // Sweeping roof with upturned eaves.
    const ry = y - h;
    const rw = w * 1.65;
    const roof = () => {
      ctx.beginPath();
      ctx.moveTo(-rw / 2, ry - rt * 1.3);
      ctx.quadraticCurveTo(-rw * 0.4, ry + rt * 0.1, -rw * 0.28, ry);
      ctx.lineTo(rw * 0.28, ry);
      ctx.quadraticCurveTo(rw * 0.4, ry + rt * 0.1, rw / 2, ry - rt * 1.3);
      ctx.quadraticCurveTo(rw * 0.32, ry - rt * 0.9, rw * 0.22, ry - rt * 1.7);
      ctx.lineTo(-rw * 0.22, ry - rt * 1.7);
      ctx.quadraticCurveTo(-rw * 0.32, ry - rt * 0.9, -rw / 2, ry - rt * 1.3);
      ctx.closePath();
    };
    roof();
    ctx.fillStyle = bodyGradient(ctx, b.accent, ry - rt * 1.7, ry, 0.2, -0.12);
    ctx.fill();
    strokeRim(ctx, rimOf(b.accent), b.size);
    ctx.beginPath();
    ctx.moveTo(-rw * 0.28, ry);
    ctx.lineTo(rw * 0.28, ry);
    ctx.strokeStyle = b.trim;
    ctx.lineWidth = Math.max(1.5, rt * 0.3);
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.beginPath();
    circlePath(ctx, -rw / 2, ry - rt * 1.35, rt * 0.22);
    circlePath(ctx, rw / 2, ry - rt * 1.35, rt * 0.22);
    ctx.fillStyle = b.trim;
    ctx.fill();
    y = ry - rt * 1.7;
  });
  // Golden finial.
  ctx.beginPath();
  ctx.moveTo(-s * 0.018, y);
  ctx.lineTo(0, y - s * 0.12);
  ctx.lineTo(s * 0.018, y);
  ctx.closePath();
  ctx.fillStyle = b.trim;
  ctx.fill();
  strokeRim(ctx, rimOf(b.trim), b.size, 0.7);
  ctx.beginPath();
  for (const f of [0.3, 0.55]) circlePath(ctx, 0, y - s * 0.12 * f, s * 0.018 * (1 - f * 0.5));
  ctx.fillStyle = b.trim;
  ctx.fill();
  strokeRim(ctx, rimOf(b.trim), b.size, 0.6);
}

function ship(ctx: Ctx, b: Build): void {
  const { s, time } = b;
  const wood = b.color;
  const rim = rimOf(wood);
  const deck = -s * 0.22;
  // Masts and sails first so the hull overlaps their feet.
  const masts: [number, number][] = [
    [-s * 0.26, s * 0.62],
    [s * 0.02, s * 0.76],
    [s * 0.28, s * 0.58],
  ];
  ctx.strokeStyle = shade(wood, -0.25);
  ctx.lineCap = 'round';
  // Rigging.
  ctx.lineWidth = Math.max(1, s * 0.004);
  ctx.beginPath();
  ctx.moveTo(masts[0]![0], deck - masts[0]![1]);
  ctx.lineTo(-s * 0.5, deck - s * 0.06);
  ctx.moveTo(masts[2]![0], deck - masts[2]![1]);
  ctx.lineTo(s * 0.66, deck - s * 0.1);
  ctx.moveTo(masts[1]![0], deck - masts[1]![1]);
  ctx.lineTo(masts[2]![0], deck - masts[2]![1]);
  ctx.moveTo(masts[1]![0], deck - masts[1]![1]);
  ctx.lineTo(masts[0]![0], deck - masts[0]![1]);
  ctx.stroke();
  for (const [mx, mh] of masts) {
    ctx.beginPath();
    ctx.moveTo(mx, deck);
    ctx.lineTo(mx, deck - mh);
    ctx.strokeStyle = shade(wood, -0.2);
    ctx.lineWidth = Math.max(1.5, s * 0.02);
    ctx.stroke();
    const billow = time ? Math.sin(time * 0.8 + mx) * s * 0.008 : 0;
    const sails: [number, number, number][] = [
      [deck - mh * 0.28, deck - mh * 0.58, s * 0.3],
      [deck - mh * 0.64, deck - mh * 0.9, s * 0.23],
    ];
    sails.forEach(([y2, y1, sw], k) => {
      const sail = () => {
        ctx.beginPath();
        ctx.moveTo(mx - sw / 2, y1);
        ctx.quadraticCurveTo(mx, y1 - s * 0.015, mx + sw / 2, y1);
        ctx.quadraticCurveTo(mx + sw / 2 + s * 0.05 + billow, (y1 + y2) / 2, mx + sw * 0.46, y2);
        ctx.quadraticCurveTo(mx, y2 + s * 0.05 + billow, mx - sw * 0.46, y2);
        ctx.quadraticCurveTo(mx - sw / 2 + s * 0.02, (y1 + y2) / 2, mx - sw / 2, y1);
        ctx.closePath();
      };
      sail();
      ctx.fillStyle = vGradient(ctx, y1, y2, [b.accent, shade(b.accent, -0.08)]);
      ctx.fill();
      if (k === 0) {
        ctx.save();
        ctx.clip();
        ctx.fillStyle = rgba(b.trim, 0.9);
        ctx.fillRect(mx - sw * 0.08, y1 - s * 0.05, sw * 0.16, y2 - y1 + s * 0.12);
        ctx.restore();
      }
      sail();
      strokeRim(ctx, rimOf(b.accent), b.size);
      ctx.beginPath();
      ctx.moveTo(mx - sw * 0.55, y1);
      ctx.lineTo(mx + sw * 0.55, y1);
      ctx.strokeStyle = shade(wood, -0.2);
      ctx.lineWidth = Math.max(1.5, s * 0.014);
      ctx.stroke();
    });
    pennant(ctx, mx, deck - mh, s * 0.1, b.trim, b);
  }
  // Hull.
  const hull = () => {
    ctx.beginPath();
    ctx.moveTo(-s * 0.52, deck - s * 0.1);
    ctx.lineTo(-s * 0.32, deck - s * 0.1);
    ctx.lineTo(-s * 0.3, deck);
    ctx.lineTo(s * 0.4, deck);
    ctx.quadraticCurveTo(s * 0.5, deck, s * 0.58, deck - s * 0.08);
    ctx.quadraticCurveTo(s * 0.52, -s * 0.06, s * 0.3, 0);
    ctx.lineTo(-s * 0.34, 0);
    ctx.quadraticCurveTo(-s * 0.5, -s * 0.06, -s * 0.52, deck - s * 0.1);
    ctx.closePath();
  };
  hull();
  ctx.fillStyle = bodyGradient(ctx, wood, deck - s * 0.1, 0, 0.14, -0.14);
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.fillStyle = b.trim;
  ctx.fillRect(-s * 0.6, deck + s * 0.045, s * 1.3, s * 0.035);
  ctx.strokeStyle = rgba(shade(wood, 0.2), 0.6);
  ctx.lineWidth = Math.max(1, s * 0.006);
  ctx.beginPath();
  for (const y of [-s * 0.07, -s * 0.035]) {
    ctx.moveTo(-s * 0.6, y);
    ctx.lineTo(s * 0.7, y);
  }
  ctx.stroke();
  ctx.restore();
  hull();
  strokeRim(ctx, rim, b.size);
  ctx.beginPath();
  for (const x of [-s * 0.18, -s * 0.04, s * 0.1, s * 0.24]) circlePath(ctx, x, deck + s * 0.11, s * 0.022);
  ctx.fillStyle = shade(wood, -0.5);
  ctx.fill();
  ctx.strokeStyle = '#ffd43b';
  ctx.lineWidth = Math.max(1, s * 0.008);
  ctx.stroke();
  // Bowsprit.
  ctx.beginPath();
  ctx.moveTo(s * 0.54, deck - s * 0.06);
  ctx.lineTo(s * 0.68, deck - s * 0.12);
  ctx.strokeStyle = shade(wood, -0.2);
  ctx.lineWidth = Math.max(1.5, s * 0.014);
  ctx.stroke();
  // Foam along the waterline.
  ctx.beginPath();
  ellipsePath(ctx, 0, -s * 0.005, s * 0.52, s * 0.018);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.fill();
}

function treehouse(ctx: Ctx, b: Build): void {
  const { s } = b;
  const bark = mix(shade(b.color, -0.38), '#6b4a30', 0.4);
  const leaves = b.trim;
  // Root flares, then the trunk.
  ctx.beginPath();
  ctx.moveTo(-s * 0.16, s * 0.005);
  ctx.quadraticCurveTo(-s * 0.08, -s * 0.03, -s * 0.07, -s * 0.12);
  ctx.lineTo(s * 0.07, -s * 0.12);
  ctx.quadraticCurveTo(s * 0.08, -s * 0.03, s * 0.16, s * 0.005);
  ctx.closePath();
  ctx.fillStyle = bark;
  ctx.fill();
  strokeRim(ctx, rimOf(bark), b.size);
  ctx.beginPath();
  ribbonPath(
    ctx,
    [
      { x: 0, y: 0 },
      { x: -s * 0.01, y: -s * 0.45 },
      { x: s * 0.01, y: -s * 0.92 },
    ],
    (t) => s * (0.15 - 0.05 * t),
  );
  ctx.fillStyle = cylinder(ctx, -s * 0.08, s * 0.08, bark);
  ctx.fill();
  strokeRim(ctx, rimOf(bark), b.size);
  // Leafy crown behind the cabin.
  const crown: Circle[] = [
    { x: 0, y: -s * 0.9, r: s * 0.2 },
    { x: -s * 0.24, y: -s * 0.82, r: s * 0.15 },
    { x: s * 0.25, y: -s * 0.83, r: s * 0.16 },
    { x: -s * 0.13, y: -s * 1.0, r: s * 0.13 },
    { x: s * 0.14, y: -s * 1.0, r: s * 0.13 },
  ];
  circleBlob(ctx, crown, bodyGradient(ctx, leaves, -s * 1.13, -s * 0.66, 0.18, -0.14), rimOf(leaves), b.size);
  gloss(ctx, -s * 0.12, -s * 1.05, s * 0.05, s * 0.025, 0.4);
  // Support struts, deck and a rope ladder.
  const deckY = -s * 0.55;
  ctx.beginPath();
  ctx.moveTo(-s * 0.05, -s * 0.4);
  ctx.lineTo(-s * 0.26, deckY + s * 0.03);
  ctx.moveTo(s * 0.05, -s * 0.4);
  ctx.lineTo(s * 0.26, deckY + s * 0.03);
  ctx.strokeStyle = shade(bark, -0.1);
  ctx.lineWidth = Math.max(1.5, s * 0.022);
  ctx.lineCap = 'round';
  ctx.stroke();
  ctx.beginPath();
  for (const x of [-s * 0.2, -s * 0.14]) {
    ctx.moveTo(x, deckY + s * 0.04);
    ctx.lineTo(x, -s * 0.02);
  }
  for (let y = deckY + s * 0.09; y < -s * 0.02; y += s * 0.06) {
    ctx.moveTo(-s * 0.2, y);
    ctx.lineTo(-s * 0.14, y);
  }
  ctx.strokeStyle = '#a0764f';
  ctx.lineWidth = Math.max(1, s * 0.008);
  ctx.stroke();
  box(ctx, -s * 0.33, deckY, s * 0.66, s * 0.045, s * 0.012, bodyGradient(ctx, b.color, deckY, deckY + s * 0.045), rimOf(b.color), b.size);
  // Cabin.
  const cw = s * 0.36;
  const ch = s * 0.19;
  const cy = deckY - ch;
  box(ctx, -cw / 2, cy, cw, ch, s * 0.01, bodyGradient(ctx, shade(b.color, 0.06), cy, deckY), rimOf(b.color), b.size);
  ctx.beginPath();
  for (let y = cy + ch * 0.25; y < deckY; y += ch * 0.25) {
    ctx.moveTo(-cw / 2 + 2, y);
    ctx.lineTo(cw / 2 - 2, y);
  }
  ctx.strokeStyle = rgba(shade(b.color, -0.25), 0.5);
  ctx.lineWidth = Math.max(1, s * 0.004);
  ctx.stroke();
  opening(ctx, cw * 0.22, deckY, cw * 0.22, ch * 0.72, shade(b.color, -0.5), rimOf(b.color), b.size);
  ctx.beginPath();
  circlePath(ctx, -cw * 0.2, cy + ch * 0.45, ch * 0.2);
  ctx.fillStyle = '#bfeaff';
  ctx.fill();
  ctx.strokeStyle = b.trim;
  ctx.lineWidth = Math.max(1.5, s * 0.012);
  ctx.stroke();
  gloss(ctx, -cw * 0.24, cy + ch * 0.38, ch * 0.07, ch * 0.04, 0.8);
  // Roof.
  ctx.beginPath();
  ctx.moveTo(-cw * 0.66, cy + s * 0.012);
  ctx.quadraticCurveTo(-cw * 0.3, cy - s * 0.09, 0, cy - s * 0.15);
  ctx.quadraticCurveTo(cw * 0.3, cy - s * 0.09, cw * 0.66, cy + s * 0.012);
  ctx.closePath();
  ctx.fillStyle = bodyGradient(ctx, b.accent, cy - s * 0.15, cy, 0.18, -0.08);
  ctx.fill();
  strokeRim(ctx, rimOf(b.accent), b.size);
  gloss(ctx, -cw * 0.25, cy - s * 0.07, cw * 0.1, s * 0.012, 0.45, -0.3);
  // Railing.
  ctx.beginPath();
  for (const x of [-s * 0.31, -s * 0.24, s * 0.24, s * 0.31]) {
    ctx.moveTo(x, deckY);
    ctx.lineTo(x, deckY - s * 0.05);
  }
  ctx.moveTo(-s * 0.32, deckY - s * 0.05);
  ctx.lineTo(-s * 0.2, deckY - s * 0.05);
  ctx.moveTo(s * 0.2, deckY - s * 0.05);
  ctx.lineTo(s * 0.32, deckY - s * 0.05);
  ctx.strokeStyle = shade(b.color, -0.2);
  ctx.lineWidth = Math.max(1, s * 0.01);
  ctx.stroke();
}

function tent(ctx: Ctx, x: number, S: number, b: Build): void {
  const ww = S * 0.8;
  const wallH = S * 0.42;
  const stripes = (x0: number, x1: number, y0: number, y1: number, converge: boolean) => {
    ctx.fillStyle = b.accent;
    ctx.beginPath();
    const n = 8;
    for (let i = 0; i < n; i += 2) {
      const a0 = x0 + ((x1 - x0) * i) / n;
      const a1 = x0 + ((x1 - x0) * (i + 1)) / n;
      if (converge) {
        ctx.moveTo(x, y0);
        ctx.lineTo(a0, y1);
        ctx.lineTo(a1, y1);
      } else {
        ctx.rect(a0, y0, a1 - a0, y1 - y0);
      }
      ctx.closePath();
    }
    ctx.fill();
  };
  // Walls.
  ctx.beginPath();
  ctx.rect(x - ww / 2, -wallH, ww, wallH);
  ctx.fillStyle = b.color;
  ctx.fill();
  ctx.save();
  ctx.clip();
  stripes(x - ww / 2, x + ww / 2, -wallH, 0, false);
  ctx.fillStyle = rgba('#000000', 0.06);
  ctx.fillRect(x + ww * 0.15, -wallH, ww * 0.35, wallH);
  ctx.restore();
  ctx.beginPath();
  ctx.rect(x - ww / 2, -wallH, ww, wallH);
  strokeRim(ctx, rimOf(b.accent), b.size);
  // Door flaps.
  ctx.beginPath();
  ctx.moveTo(x - S * 0.1, 0);
  ctx.lineTo(x, -wallH * 0.8);
  ctx.lineTo(x + S * 0.1, 0);
  ctx.closePath();
  ctx.fillStyle = mix(shade(b.accent, -0.55), '#3b2a4a', 0.3);
  ctx.fill();
  strokeRim(ctx, rimOf(b.accent), b.size, 0.8);
  // Peaked roof with converging stripes.
  const ey = -wallH;
  const rw = S * 0.96;
  const peak = -S * 0.9;
  const roof = () => {
    ctx.beginPath();
    ctx.moveTo(x - rw / 2, ey);
    ctx.quadraticCurveTo(x - rw * 0.12, ey - S * 0.12, x, peak);
    ctx.quadraticCurveTo(x + rw * 0.12, ey - S * 0.12, x + rw / 2, ey);
    ctx.closePath();
  };
  roof();
  ctx.fillStyle = b.color;
  ctx.fill();
  ctx.save();
  ctx.clip();
  stripes(x - rw / 2, x + rw / 2, peak, ey + 1, true);
  ctx.restore();
  roof();
  strokeRim(ctx, rimOf(b.accent), b.size);
  // Scalloped valance.
  const sc = 6;
  const sw = rw / sc;
  ctx.beginPath();
  for (let i = 0; i < sc; i++) {
    const x0 = x - rw / 2 + i * sw;
    ctx.moveTo(x0, ey);
    ctx.arc(x0 + sw / 2, ey, sw / 2, Math.PI, 0, true);
  }
  ctx.fillStyle = b.trim;
  ctx.fill();
  strokeRim(ctx, rimOf(b.trim), b.size, 0.7);
  pennant(ctx, x, peak, S * 0.16, b.trim, b);
}

function tents(ctx: Ctx, b: Build): void {
  tent(ctx, b.s * 0.72, b.s * 0.72, b);
  tent(ctx, 0, b.s, b);
}

function seaArch(ctx: Ctx, b: Build): void {
  const { s } = b;
  const side: Point[] = [
    { x: -0.7, y: 0 },
    { x: -0.74, y: -0.3 },
    { x: -0.66, y: -0.62 },
    { x: -0.52, y: -0.88 },
    { x: -0.3, y: -1.0 },
    { x: 0.05, y: -0.98 },
    { x: 0.36, y: -0.92 },
    { x: 0.57, y: -0.74 },
    { x: 0.69, y: -0.44 },
    { x: 0.72, y: 0 },
  ].map((p) => ({ x: p.x * s, y: p.y * s }));
  const rock = () => {
    ctx.beginPath();
    smoothLine(ctx, side);
    ctx.lineTo(s * 0.22, 0);
    ctx.bezierCurveTo(s * 0.22, -s * 0.4, s * 0.12, -s * 0.56, -s * 0.05, -s * 0.57);
    ctx.bezierCurveTo(-s * 0.24, -s * 0.56, -s * 0.33, -s * 0.4, -s * 0.33, 0);
    ctx.closePath();
  };
  rock();
  const g = ctx.createLinearGradient(-s * 0.7, -s, s * 0.7, 0);
  g.addColorStop(0, shade(b.color, 0.14));
  g.addColorStop(0.55, b.color);
  g.addColorStop(1, shade(b.color, -0.14));
  ctx.fillStyle = g;
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.strokeStyle = rgba(b.trim, 0.8);
  ctx.lineWidth = Math.max(1.5, s * 0.014);
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (let k = 1; k <= 6; k++) {
    const y = -s * (0.13 * k);
    ctx.moveTo(-s * 0.8, y);
    ctx.quadraticCurveTo(0, y - s * 0.04 * (k % 2 ? 1 : -1), s * 0.8, y + s * 0.02);
  }
  ctx.stroke();
  ctx.restore();
  rock();
  strokeRim(ctx, rimOf(b.color), b.size);
  // Grassy top.
  const cap: Circle[] = side.slice(2, 9).map((p, i) => ({ x: p.x * 0.96, y: p.y + s * 0.02, r: s * (0.06 + 0.02 * hash01(b.seed, i)) }));
  circleBlob(ctx, cap, bodyGradient(ctx, b.accent, -s * 1.08, -s * 0.72, 0.18, -0.08), rimOf(b.accent), b.size);
  // Surf around the feet and through the hole.
  ctx.beginPath();
  ellipsePath(ctx, -s * 0.52, 0, s * 0.26, s * 0.03);
  ellipsePath(ctx, s * 0.47, 0, s * 0.3, s * 0.03);
  ellipsePath(ctx, -s * 0.055, 0, s * 0.24, s * 0.022);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.fill();
}

const STRUCTURES: Record<StructureType, (ctx: Ctx, b: Build) => void> = {
  temple,
  ruins,
  castle,
  huts,
  lighthouse,
  pagoda,
  ship,
  treehouse,
  tents,
  arch: seaArch,
};

/** Footprint width (× size) used for the mound and the painted extent. */
const FOOTPRINT: Record<StructureType, number> = {
  temple: 1.15,
  ruins: 1.2,
  castle: 1.0,
  huts: 2.4,
  lighthouse: 0.6,
  pagoda: 0.85,
  ship: 1.3,
  treehouse: 0.7,
  tents: 1.7,
  arch: 1.5,
};

function drawStructure(ctx: Ctx, layer: StructureLayer, env: PaintEnv, time: number): void {
  const { size, seed } = env;
  const s = layer.size * size.unit;
  const x = layer.x * size.width;
  const base = layer.baseline * size.height;
  ctx.save();
  ctx.translate(x, base);
  if (layer.bob) {
    ctx.translate(0, Math.sin(time * 0.9) * s * 0.01);
    ctx.rotate(Math.sin(time * 0.7) * 0.022);
  }
  if (layer.mound) mound(ctx, layer.moundStyle ?? 'hill', s * FOOTPRINT[layer.type], s, layer.mound, size, seed);
  STRUCTURES[layer.type](ctx, {
    s,
    color: layer.color,
    accent: layer.accent,
    trim: layer.trim ?? shade(layer.accent, -0.2),
    size,
    seed,
    time,
  });
  ctx.restore();
}

/** How far (px) below its base line a structure paints: hull, stilts or the mound it stands on. */
function depthBelowBase(layer: StructureLayer, s: number): number {
  const own = s * 0.35;
  if (!layer.mound) return own;
  const footprint = s * FOOTPRINT[layer.type];
  const style = layer.moundStyle ?? 'hill';
  if (style === 'cloud') return Math.max(own, footprint * 0.5);
  const m = moundShape(style, footprint, s);
  return Math.max(own, m.depth * (1 + ROCK_JAG));
}

export const structurePainter: Painter<StructureLayer> = {
  extent: (layer, { size }) => {
    const s = layer.size * size.unit;
    const base = layer.baseline * size.height;
    return { top: base - s * 1.3, bottom: base + depthBelowBase(layer, s) };
  },
  still(ctx, layer, env) {
    if (!layer.bob) drawStructure(ctx, layer, env, 0);
  },
  moving(ctx, layer, env) {
    if (layer.bob) drawStructure(ctx, layer, env, env.time);
  },
  animated: (layer) => layer.bob === true,
};

// ───────────── rope bridge ─────────────

function bridgeEnds(layer: BridgeLayer, size: SceneSize): { a: Point; b: Point; c: Point } {
  const a = { x: layer.from.x * size.width, y: layer.from.y * size.height - (layer.from.rise ?? 0) * size.unit };
  const b = { x: layer.to.x * size.width, y: layer.to.y * size.height - (layer.to.rise ?? 0) * size.unit };
  // The quadratic's midpoint dips by `sag` when its control point dips by 2·sag.
  const c = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 + 2 * layer.sag * size.unit };
  return { a, b, c };
}

export const bridgePainter: Painter<BridgeLayer> = {
  extent: (layer, { size }) => {
    const { a, b } = bridgeEnds(layer, size);
    return { top: Math.min(a.y, b.y) - size.unit * 0.1, bottom: Math.max(a.y, b.y) + layer.sag * size.unit + size.unit * 0.05 };
  },
  still(ctx, layer, { size }) {
    const { a, b, c } = bridgeEnds(layer, size);
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    const n = Math.max(4, Math.round(dist / (size.unit * 0.022)));
    const rail = size.unit * 0.055;
    const rope = Math.max(1, size.unit * 0.004);
    const railCurve = { a: { x: a.x, y: a.y - rail }, c: { x: c.x, y: c.y - rail * 0.6 }, b: { x: b.x, y: b.y - rail } };
    // Handrail and ties behind the planks.
    ctx.lineCap = 'round';
    ctx.strokeStyle = layer.rope;
    ctx.lineWidth = rope;
    ctx.beginPath();
    for (let i = 0; i <= n; i += 3) {
      const t = i / n;
      const d = quadPoint(a, c, b, t);
      const r = quadPoint(railCurve.a, railCurve.c, railCurve.b, t);
      ctx.moveTo(d.x, d.y);
      ctx.lineTo(r.x, r.y);
    }
    ctx.stroke();
    // Planks, each turned along the curve.
    const pw = (dist / n) * 0.78;
    const ph = size.unit * 0.012;
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const p = quadPoint(a, c, b, t);
      const q = quadPoint(a, c, b, Math.min(1, t + 0.01));
      const ang = Math.atan2(q.y - p.y, q.x - p.x);
      const cos = Math.cos(ang);
      const sin = Math.sin(ang);
      const corner = (u: number, v: number) => ({ x: p.x + u * cos - v * sin, y: p.y + u * sin + v * cos });
      const pts = [corner(-pw / 2, -ph / 2), corner(pw / 2, -ph / 2), corner(pw / 2, ph / 2), corner(-pw / 2, ph / 2)];
      ctx.moveTo(pts[0]!.x, pts[0]!.y);
      for (const pt of pts.slice(1)) ctx.lineTo(pt.x, pt.y);
      ctx.closePath();
    }
    ctx.fillStyle = layer.plank;
    ctx.fill();
    ctx.lineJoin = 'round';
    strokeRim(ctx, rimOf(layer.plank), size, 0.7);
    // Deck rope and handrail on top.
    ctx.beginPath();
    ctx.moveTo(a.x, a.y + ph * 0.5);
    ctx.quadraticCurveTo(c.x, c.y + ph * 0.5, b.x, b.y + ph * 0.5);
    ctx.moveTo(railCurve.a.x, railCurve.a.y);
    ctx.quadraticCurveTo(railCurve.c.x, railCurve.c.y, railCurve.b.x, railCurve.b.y);
    ctx.strokeStyle = layer.rope;
    ctx.lineWidth = rope * 1.4;
    ctx.stroke();
  },
};
